import React from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Badge, Select, Switch, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { StatsData, TestRun, TestResult, TestRunFilters } from '../../../services/devApi';

interface TestSummaryPanelProps {
  stats: StatsData | null;
  testRuns: TestRun[];
  testRunsTotal: number;
  loading: boolean;
  filters: TestRunFilters;
  onFilterChange: (filters: Partial<TestRunFilters>) => void;
}

const CATEGORY_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'validator', label: 'Validator' },
  { value: 'service', label: 'Service' },
  { value: 'integration', label: 'Integration' },
  { value: 'e2e', label: 'E2E' },
];

function formatDuration(ms: number): string {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(2)}s`;
  }
  return `${ms}ms`;
}

function formatRunAt(dateStr: string): string {
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}-${dd} ${hh}:${min}`;
}

function getCategoryTagColor(category: string): string {
  const map: Record<string, string> = {
    validator: 'blue',
    service: 'cyan',
    integration: 'purple',
    e2e: 'geekblue',
  };
  return map[category] || 'default';
}

const TestSummaryPanel: React.FC<TestSummaryPanelProps> = ({
  stats,
  testRuns,
  testRunsTotal,
  loading,
  filters,
  onFilterChange,
}) => {
  const beTests = stats?.tests?.backend;
  const e2eTests = stats?.tests?.e2e;

  // Outer test run columns
  const runColumns: ColumnsType<TestRun> = [
    {
      title: '執行時間',
      dataIndex: 'runAt',
      key: 'runAt',
      width: 110,
      render: (runAt: string) => formatRunAt(runAt),
    },
    {
      title: '分類',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (cat: string) => <Tag color={getCategoryTagColor(cat)}>{cat}</Tag>,
    },
    {
      title: '模組',
      dataIndex: 'formId',
      key: 'formId',
      width: 120,
      render: (fid: string) => <span className="font-mono">{fid}</span>,
    },
    {
      title: '結果',
      key: 'result',
      width: 120,
      render: (_: unknown, record: TestRun) => {
        const hasFail = record.failCount > 0;
        return (
          <span className={hasFail ? 'text-red-600 font-bold' : 'text-green-600'}>
            {hasFail ? 'FAIL' : 'PASS'} {record.passCount}/{record.totalTests}
          </span>
        );
      },
    },
    {
      title: '耗時',
      dataIndex: 'totalDurationMs',
      key: 'totalDurationMs',
      width: 90,
      render: (ms: number) => formatDuration(ms),
    },
  ];

  // Inner expanded row (test results)
  const expandedRowRender = (run: TestRun) => {
    if (!run.results || run.results.length === 0) {
      return <Empty description="無測試明細" />;
    }

    // Find the slowest test for highlighting
    const maxDuration = Math.max(...run.results.map((r) => r.durationMs));

    const innerColumns: ColumnsType<TestResult> = [
      { title: '測試名稱', dataIndex: 'testName', key: 'testName' },
      {
        title: '結果',
        dataIndex: 'pass',
        key: 'pass',
        width: 90,
        render: (pass: boolean) =>
          pass ? (
            <Badge status="success" text="PASS" />
          ) : (
            <Badge status="error" text="FAIL" />
          ),
      },
      {
        title: '耗時',
        dataIndex: 'durationMs',
        key: 'durationMs',
        width: 90,
        render: (ms: number) => formatDuration(ms),
      },
    ];

    return (
      <Table<TestResult>
        dataSource={run.results}
        columns={innerColumns}
        size="small"
        pagination={false}
        rowKey="testName"
        scroll={{ x: 600 }}
        rowClassName={(record) => {
          if (record.durationMs > 5000) return 'bg-red-50';
          if (record.durationMs === maxDuration && maxDuration > 0) return 'bg-orange-50';
          return '';
        }}
      />
    );
  };

  return (
    <Card title="測試結果" loading={loading && !stats}>
      <Row gutter={16}>
        {/* Left column: summary cards */}
        <Col xs={24} md={8}>
          <div className="space-y-4">
            <Card size="small" title="後端測試">
              {beTests ? (
                <>
                  <Statistic
                    value={beTests.passCount}
                    suffix="PASS"
                    valueStyle={{ color: '#52c41a' }}
                  />
                  {beTests.failCount > 0 ? (
                    <Statistic
                      value={beTests.failCount}
                      suffix="FAIL"
                      valueStyle={{ color: '#ff4d4f', fontSize: 14 }}
                    />
                  ) : (
                    <div className="text-gray-400 text-sm">0 FAIL</div>
                  )}
                  <div className="text-xs text-gray-400 mt-2">
                    最後執行：{beTests.lastRunAt ? formatRunAt(beTests.lastRunAt) : '--'}
                  </div>
                </>
              ) : (
                <Empty description="無資料" />
              )}
            </Card>

            <Card size="small" title="E2E 測試">
              {e2eTests ? (
                <>
                  <Statistic
                    value={e2eTests.passCount}
                    suffix="PASS"
                    valueStyle={{ color: '#52c41a' }}
                  />
                  {e2eTests.failCount > 0 ? (
                    <Statistic
                      value={e2eTests.failCount}
                      suffix="FAIL"
                      valueStyle={{ color: '#ff4d4f', fontSize: 14 }}
                    />
                  ) : (
                    <div className="text-gray-400 text-sm">0 FAIL</div>
                  )}
                  <div className="text-xs text-gray-400 mt-2">
                    最後執行：{e2eTests.lastRunAt ? formatRunAt(e2eTests.lastRunAt) : '--'}
                  </div>
                </>
              ) : (
                <Empty description="無資料" />
              )}
            </Card>
          </div>
        </Col>

        {/* Right column: test run detail table */}
        <Col xs={24} md={16}>
          <div className="flex items-center gap-4 mb-4">
            <Select
              placeholder="篩選分類"
              allowClear
              style={{ width: 150 }}
              value={filters.category || undefined}
              onChange={(val) => onFilterChange({ category: val || undefined })}
              options={CATEGORY_OPTIONS}
            />
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">只看失敗</span>
              <Switch
                checked={filters.failOnly || false}
                onChange={(checked) => onFilterChange({ failOnly: checked || undefined })}
                size="small"
              />
            </div>
          </div>

          {testRuns.length > 0 ? (
            <Table<TestRun>
              dataSource={testRuns}
              columns={runColumns}
              size="small"
              rowKey="id"
              scroll={{ x: 600 }}
              pagination={{
                current: filters.page,
                pageSize: filters.size,
                total: testRunsTotal,
                showSizeChanger: false,
                onChange: (page) => onFilterChange({ page }),
              }}
              expandable={{
                expandedRowRender,
                rowExpandable: (record) =>
                  !!(record.results && record.results.length > 0),
              }}
            />
          ) : (
            <Empty description="尚無測試執行記錄" />
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default TestSummaryPanel;
